import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { OverAllowanceWarning } from "../components/OverAllowanceWarning";
import {
  Check,
  Loader2,
  CreditCard,
  Users,
  MapPin,
  TrendingUp,
  ArrowUpRight,
  AlertTriangle,
  X,
  Minus,
} from "lucide-react";

// Plan tiers and the feature-comparison matrix are served by the backend
// (GET /api/pricing) from the canonical pricing-catalog, so the dashboard and
// the marketing site never drift. Types below mirror that payload.
type Interval = "monthly" | "annual";
type PlanKey = "FREE" | "STARTER" | "PROFESSIONAL" | "BUSINESS" | "ENTERPRISE";

interface Tier {
  key: PlanKey;
  name: string;
  sub: string;
  monthly: number | null; // null = custom / contact
  annual: number | null;
  checkins: string;
  locations: string;
  seats: string;
  overagePerCheckIn: number | null;
  features: string[];
  popular?: boolean;
}

// Cell values: true = included, false = not included, string = the specific
// value/tier for that plan. Column order matches TIER_ORDER from the backend.
type Cell = boolean | string;
interface CompareRow {
  label: string;
  cells: [Cell, Cell, Cell, Cell, Cell]; // FREE, STARTER, PROFESSIONAL, BUSINESS, ENTERPRISE
}
interface CompareGroup {
  section: string;
  rows: CompareRow[];
}

interface Catalog {
  register: string;
  enterpriseWa: string;
  tiers: Tier[];
  comparison: CompareGroup[];
}

// Set by Register.tsx when a visitor arrives from a marketing pricing CTA
// (`/register?plan=…`). Consumed once here after their first successful login.
const INTENDED_PLAN_KEY = "vms_intended_plan";

interface BillingInfo {
  plan: PlanKey;
  planInterval: string | null; // Stripe "month" | "year" | null
  planStatus: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pendingPlan: PlanKey | null; // scheduled downgrade target
  pendingPlanInterval: string | null; // "month" | "year"
  pendingPlanEffectiveAt: string | null; // when the downgrade applies
  isPaid: boolean;
  limits: {
    checkInsPerMonth: number | null;
    locations: number | null;
    seats: number | null;
    overagePerCheckIn: number | null;
  };
  usage: {
    checkInsThisMonth: number;
    locations: number;
    seats: number;
    overageCheckIns: number;
    estimatedOverageCharge: number;
  };
}

const sgd = (n: number) => n.toLocaleString("en-SG");

// Tier ordering for upgrade/downgrade labelling.
const PLAN_RANK: Record<PlanKey, number> = {
  FREE: 0,
  STARTER: 1,
  PROFESSIONAL: 2,
  BUSINESS: 3,
  ENTERPRISE: 4,
};

export function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState<Interval>("monthly");
  const [redirecting, setRedirecting] = useState<PlanKey | "portal" | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<
    { kind: "success" | "canceled" | "syncing"; text: string } | null
  >(null);

  // Plan selected on the marketing site, carried through the magic-link
  // round-trip via localStorage. Read once on mount.
  const [intendedPlan, setIntendedPlan] = useState<PlanKey | null>(() => {
    const raw = localStorage.getItem(INTENDED_PLAN_KEY);
    if (!raw) return null;
    const up = raw.toUpperCase();
    return (["STARTER", "PROFESSIONAL", "BUSINESS"] as string[]).includes(up)
      ? (up as PlanKey)
      : null;
  });

  const fetchBilling = useCallback(async () => {
    const res = await apiGet<BillingInfo>("/billing");
    if (res.success && res.data) {
      setInfo(res.data);
      if (res.data.planInterval === "year") setInterval("annual");
      return res.data;
    }
    return null;
  }, []);

  useEffect(() => {
    // Pricing catalog is public and served outside the /api/dashboard auth base,
    // so fetch it directly rather than via apiGet.
    fetch("/api/pricing")
      .then((r) => r.json())
      .then((res) => {
        if (res.success && res.data) setCatalog(res.data as Catalog);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchBilling().finally(() => setLoading(false));
  }, [fetchBilling]);

  // Handle the Stripe Checkout return. On success the subscription webhook may
  // lag a second or two behind the redirect, so poll briefly until the plan
  // flips to paid (or we give up and show the last known state).
  const returnHandled = useRef(false);
  useEffect(() => {
    if (returnHandled.current) return;
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    if (!success && !canceled) return;
    returnHandled.current = true;

    // Clear the query params so a refresh doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete("success");
    next.delete("canceled");
    next.delete("session_id");
    setSearchParams(next, { replace: true });

    if (canceled) {
      setBanner({ kind: "canceled", text: "Checkout canceled — your plan is unchanged." });
      return;
    }

    // success: clear any intended-plan prompt and poll for the upgrade.
    localStorage.removeItem(INTENDED_PLAN_KEY);
    setIntendedPlan(null);
    setBanner({ kind: "syncing", text: "Confirming your subscription…" });
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      const data = await fetchBilling();
      if (data?.isPaid && ["active", "trialing"].includes(data.planStatus ?? "")) {
        setBanner({ kind: "success", text: `You're now on the ${titleCase(data.plan)} plan.` });
        return;
      }
      if (attempts >= 6) {
        setBanner({
          kind: "success",
          text: "Payment received. Your plan will update here shortly.",
        });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
  }, [searchParams, setSearchParams, fetchBilling]);

  async function startCheckout(plan: PlanKey) {
    if (plan === "FREE" || plan === "ENTERPRISE") return;
    setError(null);
    setRedirecting(plan);

    // Already subscribed → switch the plan in place (Stripe prorates); no new
    // Checkout. Otherwise send them through Checkout to start a subscription.
    const subscribed =
      Boolean(info?.isPaid) && ["active", "trialing", "past_due"].includes(info?.planStatus ?? "");

    if (subscribed) {
      const res = await apiPost<{ mode: "upgraded" | "scheduled_downgrade"; effectiveAt?: string }>(
        "/billing/change-plan",
        { plan, interval },
      );
      if (res.success && res.data) {
        const targetInterval = interval === "annual" ? "year" : "month";
        // Downgrades don't take effect now — they're scheduled for period end.
        if (res.data.mode === "scheduled_downgrade") {
          const when = res.data.effectiveAt ? formatDate(res.data.effectiveAt) : "the renewal date";
          setBanner({
            kind: "success",
            text: `${titleCase(plan)} plan scheduled from ${when}. You keep your current plan until then.`,
          });
          let attempts = 0;
          const poll = async () => {
            attempts += 1;
            const data = await fetchBilling();
            if (data?.pendingPlan === plan || attempts >= 6) {
              setRedirecting(null);
              return;
            }
            setTimeout(poll, 1500);
          };
          poll();
          return;
        }
        setBanner({ kind: "syncing", text: "Updating your subscription…" });
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          const data = await fetchBilling();
          if (data?.plan === plan && data?.planInterval === targetInterval) {
            setBanner({
              kind: "success",
              text: `You're now on the ${titleCase(plan)} plan. The prorated difference for the current period has been charged to your card.`,
            });
            setRedirecting(null);
            return;
          }
          if (attempts >= 6) {
            setBanner({ kind: "success", text: "Plan change received. It'll update here shortly." });
            setRedirecting(null);
            return;
          }
          setTimeout(poll, 1500);
        };
        poll();
        return;
      }
      setError(res.error?.message || "Could not change your plan. Please try again.");
      setRedirecting(null);
      return;
    }

    const res = await apiPost<{ url: string }>("/billing/checkout", { plan, interval });
    if (res.success && res.data?.url) {
      localStorage.removeItem(INTENDED_PLAN_KEY);
      window.location.href = res.data.url;
      return;
    }
    setError(res.error?.message || "Could not start checkout. Please try again.");
    setRedirecting(null);
  }

  async function openPortal(flow?: "cancel") {
    setError(null);
    setRedirecting("portal");
    const res = await apiPost<{ url: string }>("/billing/portal", flow ? { flow } : {});
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
      return;
    }
    setError(res.error?.message || "Could not open the billing portal.");
    setRedirecting(null);
  }

  const [cancelingSchedule, setCancelingSchedule] = useState(false);
  async function cancelScheduledChange() {
    setError(null);
    setCancelingSchedule(true);
    const res = await apiPost("/billing/cancel-scheduled-change", {});
    if (res.success) {
      await fetchBilling();
      setBanner({ kind: "success", text: "Scheduled plan change canceled. You'll stay on your current plan." });
    } else {
      setError(res.error?.message || "Could not cancel the scheduled change.");
    }
    setCancelingSchedule(false);
  }

  if (loading) return <div className="p-6 text-stone-500">Loading...</div>;
  if (!info) return <div className="p-6 text-stone-500">Failed to load billing information.</div>;

  const hasSubscription = info.isPaid && Boolean(info.planStatus);
  const renewLabel = info.cancelAtPeriodEnd ? "Cancels on" : "Renews on";

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6 font-display">
        Billing & Plan
      </h1>

      {banner && (
        <div
          className={`flex items-start gap-2 px-4 py-3 rounded-xl mb-5 text-sm ${
            banner.kind === "canceled"
              ? "bg-amber-50 text-amber-800 border border-amber-100"
              : banner.kind === "syncing"
                ? "bg-stone-100 text-stone-700 border border-stone-200"
                : "bg-green-50 text-green-800 border border-green-100"
          }`}
        >
          {banner.kind === "syncing" ? (
            <Loader2 size={18} className="animate-spin shrink-0 mt-0.5" />
          ) : banner.kind === "success" ? (
            <Check size={18} className="shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          )}
          <span className="flex-1">{banner.text}</span>
          <button onClick={() => setBanner(null)} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
            <X size={16} />
          </button>
        </div>
      )}

      {intendedPlan && !info.isPaid && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 rounded-xl mb-5 bg-brand-50 border border-brand-100">
          <div className="flex-1">
            <p className="text-sm font-medium text-brand-900">
              You chose the {titleCase(intendedPlan)} plan when you signed up.
            </p>
            <p className="text-xs text-brand-700 mt-0.5">
              Continue to checkout to activate it, or pick a different plan below.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => startCheckout(intendedPlan)}
              disabled={redirecting !== null}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {redirecting === intendedPlan ? <Loader2 size={15} className="animate-spin" /> : null}
              Continue to checkout
            </button>
            <button
              onClick={() => {
                localStorage.removeItem(INTENDED_PLAN_KEY);
                setIntendedPlan(null);
              }}
              aria-label="Dismiss"
              className="text-brand-400 hover:text-brand-700"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-5">{error}</p>
      )}

      {/* Current plan card */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CreditCard size={18} className="text-brand-500" />
              <span className="text-sm text-stone-500">Current plan</span>
            </div>
            <p className="text-2xl font-semibold text-stone-900 font-display">
              {titleCase(info.plan)}
              {info.isPaid && info.planInterval && (
                <span className="text-sm font-normal text-stone-400 ml-2">
                  billed {info.planInterval === "year" ? "annually" : "monthly"}
                </span>
              )}
            </p>
            {info.currentPeriodEnd && info.isPaid && (
              <p className="text-sm text-stone-500 mt-1">
                {renewLabel}{" "}
                <span className="font-medium text-stone-700">{formatDate(info.currentPeriodEnd)}</span>
              </p>
            )}
            {info.planStatus && info.isPaid && info.planStatus !== "active" && (
              <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {statusLabel(info.planStatus)}
              </span>
            )}
          </div>
          {hasSubscription && (
            <button
              onClick={() => openPortal()}
              disabled={redirecting !== null}
              className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-50 disabled:opacity-50 transition-colors shrink-0"
            >
              {redirecting === "portal" ? <Loader2 size={15} className="animate-spin" /> : null}
              Manage subscription
            </button>
          )}
        </div>
        {info.cancelAtPeriodEnd && (
          <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>
              Your subscription is set to cancel at the end of the current period. You'll move to the
              Free plan on {info.currentPeriodEnd ? formatDate(info.currentPeriodEnd) : "the renewal date"}.
              Reactivate anytime from the billing portal.
            </span>
          </div>
        )}
        {info.pendingPlan && !info.cancelAtPeriodEnd && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span className="flex-1">
              Scheduled to change to the {titleCase(info.pendingPlan)} plan on{" "}
              {info.pendingPlanEffectiveAt ? formatDate(info.pendingPlanEffectiveAt) : "the renewal date"}.
              You keep your current plan and its limits until then.
            </span>
            <button
              onClick={cancelScheduledChange}
              disabled={cancelingSchedule}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-900 font-medium rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors shrink-0"
            >
              {cancelingSchedule ? <Loader2 size={14} className="animate-spin" /> : null}
              Cancel change
            </button>
          </div>
        )}
      </div>

      {/* Usage */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-8">
        <h2 className="text-sm font-medium text-stone-700 mb-4">Usage this month</h2>
        <div className="space-y-5">
          <UsageBar
            icon={<TrendingUp size={16} />}
            label="Check-ins"
            used={info.usage.checkInsThisMonth}
            limit={info.limits.checkInsPerMonth}
            softCap
          />
          <UsageBar
            icon={<MapPin size={16} />}
            label="Locations"
            used={info.usage.locations}
            limit={info.limits.locations}
          />
          <UsageBar
            icon={<Users size={16} />}
            label="Team seats"
            used={info.usage.seats}
            limit={info.limits.seats}
          />
        </div>
        <OverAllowanceWarning allowance={info} className="mt-4" />
      </div>

      {/* Plan grid */}
      {catalog && (
      <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium text-stone-700">Change plan</h2>
          <button
            onClick={() => setShowDetails(true)}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
          >
            See full details
          </button>
        </div>
        <div className="inline-flex items-center bg-stone-100 rounded-lg p-0.5 text-sm self-start sm:self-auto">
          <button
            onClick={() => setInterval("monthly")}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              interval === "monthly" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval("annual")}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              interval === "annual" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
            }`}
          >
            Annual
            <span className="ml-1.5 text-xs text-green-600 font-semibold">2 months free</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalog.tiers.map((tier) => (
          <PlanCard
            key={tier.key}
            tier={tier}
            interval={interval}
            enterpriseWa={catalog.enterpriseWa}
            isCurrent={tier.key === info.plan}
            isScheduled={
              tier.key === info.pendingPlan ||
              (tier.key === "FREE" && info.cancelAtPeriodEnd)
            }
            ctaLabel={
              hasSubscription && tier.key !== "ENTERPRISE" && tier.key !== "FREE"
                ? PLAN_RANK[tier.key] > PLAN_RANK[info.plan]
                  ? "Upgrade"
                  : "Downgrade"
                : `Choose ${tier.name}`
            }
            redirecting={redirecting === (tier.key === "FREE" ? "portal" : tier.key)}
            disabled={redirecting !== null}
            onSelect={() =>
              tier.key === "FREE" ? openPortal("cancel") : startCheckout(tier.key)
            }
          />
        ))}
      </div>

      {showDetails && (
        <PlanDetailsModal
          interval={interval}
          currentPlan={info.plan}
          tiers={catalog.tiers}
          comparison={catalog.comparison}
          onClose={() => setShowDetails(false)}
        />
      )}
      </>
      )}
    </div>
  );
}

function PlanDetailsModal({
  interval,
  currentPlan,
  tiers,
  comparison,
  onClose,
}: {
  interval: Interval;
  currentPlan: PlanKey;
  tiers: Tier[];
  comparison: CompareGroup[];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const priceFor = (tier: Tier) => {
    if (tier.key === "ENTERPRISE") return "Custom";
    if (tier.monthly === 0) return "S$0";
    return interval === "annual" ? `S$${sgd(tier.annual!)}/yr` : `S$${sgd(tier.monthly!)}/mo`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-stone-900/50 backdrop-blur-sm p-2 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl my-4 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-6rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-white rounded-t-2xl shrink-0">
          <h2 className="text-base sm:text-lg font-semibold text-stone-900 font-display">
            Every feature, side by side
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left font-medium text-stone-500 px-4 py-3 min-w-[180px] sticky left-0 top-0 bg-white z-30" />
                {tiers.map((tier) => (
                  <th
                    key={tier.key}
                    className={`px-3 py-3 text-center align-bottom min-w-[110px] border-b-2 sticky top-0 z-20 ${
                      tier.key === currentPlan ? "border-brand-500 bg-brand-50" : "border-stone-200 bg-white"
                    }`}
                  >
                    <div className="font-semibold text-stone-900 font-display">{tier.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{priceFor(tier)}</div>
                    {tier.key === currentPlan && (
                      <div className="inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-500 text-white uppercase tracking-wide">
                        Current
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((group) => (
                <Fragment key={group.section}>
                  <tr>
                    <td
                      colSpan={tiers.length + 1}
                      className="px-4 pt-5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-stone-400 bg-white"
                    >
                      {group.section}
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.label} className="border-b border-stone-100">
                      <td className="px-4 py-3 text-stone-600 sticky left-0 bg-white">{row.label}</td>
                      {row.cells.map((cell, i) => (
                        <td
                          key={tiers[i].key}
                          className={`px-3 py-3 text-center ${
                            tiers[i].key === currentPlan ? "bg-brand-50/40" : ""
                          }`}
                        >
                          <CompareCell value={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="px-5 py-4 text-xs text-stone-400 border-t border-stone-100">
          Prices in SGD, exclusive of GST. Annual billing saves 17% (2 months free) on every paid plan.
        </p>
      </div>
    </div>
  );
}

function CompareCell({ value }: { value: Cell }) {
  if (value === true) return <Check size={16} className="text-brand-500 inline-block" />;
  if (value === false) return <Minus size={16} className="text-stone-300 inline-block" />;
  return <span className="text-stone-700">{value}</span>;
}

function PlanCard({
  tier,
  interval,
  enterpriseWa,
  isCurrent,
  isScheduled,
  ctaLabel,
  redirecting,
  disabled,
  onSelect,
}: {
  tier: Tier;
  interval: Interval;
  enterpriseWa: string;
  isCurrent: boolean;
  isScheduled: boolean;
  ctaLabel: string;
  redirecting: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const isEnterprise = tier.key === "ENTERPRISE";
  const isFree = tier.key === "FREE";

  const priceLabel = isEnterprise
    ? "Custom"
    : interval === "annual"
      ? `S$${sgd(tier.annual!)}`
      : `S$${sgd(tier.monthly!)}`;
  const priceSuffix = isEnterprise || tier.monthly === 0 ? "" : interval === "annual" ? "/yr" : "/mo";

  return (
    <div
      className={`relative bg-white rounded-xl border p-5 flex flex-col ${
        isCurrent
          ? "border-brand-500 ring-1 ring-brand-500"
          : isScheduled
            ? "border-amber-400 ring-1 ring-amber-300"
            : tier.popular
              ? "border-brand-200"
              : "border-stone-200"
      }`}
    >
      {tier.popular && !isCurrent && !isScheduled && (
        <span className="absolute -top-2 right-4 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-500 text-white uppercase tracking-wide">
          Most popular
        </span>
      )}
      {isCurrent && (
        <span className="absolute -top-2 right-4 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-900 text-white uppercase tracking-wide">
          Current plan
        </span>
      )}
      {isScheduled && !isCurrent && (
        <span className="absolute -top-2 right-4 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500 text-white uppercase tracking-wide">
          Scheduled
        </span>
      )}
      <div className="mb-3">
        <p className="text-base font-semibold text-stone-900 font-display">{tier.name}</p>
        <p className="text-xs text-stone-400">{tier.sub}</p>
      </div>
      <div className="mb-4">
        <span className="text-2xl font-semibold text-stone-900 font-display">{priceLabel}</span>
        {priceSuffix && <span className="text-sm text-stone-400">{priceSuffix}</span>}
        {!isEnterprise && tier.monthly! > 0 && interval === "annual" && (
          <p className="text-xs text-green-600 mt-0.5">2 months free vs monthly</p>
        )}
        {tier.overagePerCheckIn !== null && (
          <p className="text-xs text-stone-500 mt-1">
            Then S${tier.overagePerCheckIn.toFixed(2)} per extra check-in
          </p>
        )}
      </div>
      <ul className="space-y-2 mb-5 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-stone-600">
            <Check size={15} className="text-brand-500 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <button
          disabled
          className="w-full py-2 text-sm font-medium rounded-lg bg-stone-100 text-stone-400 cursor-default"
        >
          Your current plan
        </button>
      ) : isScheduled ? (
        <button
          disabled
          className="w-full py-2 text-sm font-medium rounded-lg bg-amber-50 text-amber-700 cursor-default"
        >
          Scheduled
        </button>
      ) : isEnterprise ? (
        <a
          href={enterpriseWa}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
        >
          Contact us
          <ArrowUpRight size={15} />
        </a>
      ) : isFree ? (
        // Only reachable when a paid tenant views Free. Downgrading to Free =
        // canceling the subscription, so this deep-links into the portal's
        // cancel flow.
        <button
          onClick={onSelect}
          disabled={disabled}
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-50"
        >
          {redirecting ? <Loader2 size={15} className="animate-spin" /> : null}
          Cancel to downgrade
        </button>
      ) : (
        <button
          onClick={onSelect}
          disabled={disabled}
          className={`w-full inline-flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
            tier.popular
              ? "bg-brand-500 text-white hover:bg-brand-700"
              : "border border-stone-300 text-stone-700 hover:bg-stone-50"
          }`}
        >
          {redirecting ? <Loader2 size={15} className="animate-spin" /> : null}
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

function UsageBar({
  icon,
  label,
  used,
  limit,
  softCap,
}: {
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number | null;
  softCap?: boolean;
}) {
  const percent = limit ? Math.min((used / limit) * 100, 100) : 0;
  const over = limit !== null && used > limit;
  const warn = limit !== null && percent >= 80;
  const barColor = over
    ? "bg-red-500"
    : warn
      ? "bg-amber-500"
      : "bg-brand-500";

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-2 text-sm text-stone-600">
          <span className="text-stone-400">{icon}</span>
          {label}
        </span>
        <span className={`text-sm ${over ? "text-red-600 font-medium" : "text-stone-500"}`}>
          {used.toLocaleString()} / {limit === null ? "Unlimited" : limit.toLocaleString()}
        </span>
      </div>
      {limit !== null && (
        <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${percent}%` }} />
        </div>
      )}
      {over && softCap && (
        <p className="text-xs text-red-600 mt-1">Over the plan limit — check-ins are never blocked.</p>
      )}
    </div>
  );
}

function titleCase(plan: string): string {
  return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
}

function statusLabel(status: string): string {
  switch (status) {
    case "past_due":
      return "Payment past due";
    case "trialing":
      return "Trial";
    case "incomplete":
      return "Payment incomplete";
    case "unpaid":
      return "Unpaid";
    default:
      return titleCase(status.replace(/_/g, " "));
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
